import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { graphTrpc } from '@org/clients/graph';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useToast,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Badge,
} from '@org/ui';
import {
  ArrowLeft,
  Trophy,
  Calendar,
  MapPin,
  MessageCircle,
  UserPlus,
  UserMinus,
  MoreVertical,
  Flag,
  Ban,
  Shield,
  ShieldCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { ReportModal } from '@/components/report-modal';
import { BlockModal } from '@/components/block-modal';

export const Route = createFileRoute('/profile/$userId')({
  component: UserProfilePage,
});

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation('community');
  if (role === 'admin') {
    return (
      <Badge variant="default" className="gap-1 bg-brand text-white">
        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
        {t('profile.roleAdmin')}
      </Badge>
    );
  }
  if (role === 'moderator') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Shield className="h-3 w-3" aria-hidden="true" />
        {t('profile.roleModerator')}
      </Badge>
    );
  }
  return null;
}

function UserProfilePage() {
  const { userId } = Route.useParams();
  const { t } = useTranslation('community');
  const { toast } = useToast();
  // Auth context's userId is the Auth0 sub (e.g. "auth0|abc"), not the DB UUID,
  // so resolve the current user via the server-side getMyProfile call.
  const { data: myProfile } = graphTrpc.profiles.getMyProfile.useQuery();
  const isOwnProfile = myProfile?.userId === userId;

  const [optimisticFollowing, setOptimisticFollowing] = useState<boolean | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  const { data: profile, isLoading } = graphTrpc.profiles.getProfile.useQuery({ userId });
  const { data: followStatus } = graphTrpc.connections.isFollowing.useQuery(
    { userId },
    { enabled: !isOwnProfile }
  );
  const { data: connectionCounts, refetch: refetchCounts } = graphTrpc.connections.getCounts.useQuery({ userId });
  const { data: followers } = graphTrpc.connections.getFollowers.useQuery({ userId, limit: 10 });
  const { data: following } = graphTrpc.connections.getFollowing.useQuery({ userId, limit: 10 });

  const utils = graphTrpc.useUtils();

  const displayName = profile?.displayName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    'Community Member';

  const followMutation = graphTrpc.connections.follow.useMutation({
    onMutate: () => {
      setOptimisticFollowing(true);
    },
    onSuccess: () => {
      utils.connections.isFollowing.invalidate({ userId });
      refetchCounts();
      toast({ title: t('profile.followed'), description: t('profile.followedDescription', { name: displayName }) });
    },
    onError: (error) => {
      setOptimisticFollowing(null);
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
    onSettled: () => {
      setOptimisticFollowing(null);
    },
  });

  const unfollowMutation = graphTrpc.connections.unfollow.useMutation({
    onMutate: () => {
      setOptimisticFollowing(false);
    },
    onSuccess: () => {
      utils.connections.isFollowing.invalidate({ userId });
      refetchCounts();
      toast({ title: t('profile.unfollowed'), description: t('profile.unfollowedDescription', { name: displayName }) });
    },
    onError: (error) => {
      setOptimisticFollowing(null);
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
    onSettled: () => {
      setOptimisticFollowing(null);
    },
  });

  const isFollowing = optimisticFollowing ?? followStatus?.isFollowing ?? false;
  const isPending = followMutation.isPending || unfollowMutation.isPending;

  const handleToggleFollow = () => {
    if (isFollowing) {
      unfollowMutation.mutate({ userId });
    } else {
      followMutation.mutate({ userId });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('common.back')}
        </Button>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <Skeleton className="h-24 w-24 rounded-full" />
            <div className="flex-1 space-y-2 text-center sm:text-left">
              <Skeleton className="mx-auto h-6 w-48 sm:mx-0" />
              <Skeleton className="mx-auto h-4 w-32 sm:mx-0" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <Link to="/members">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('profile.backToMembers')}
          </Button>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">{t('profile.notFound')}</p>
        </div>
      </div>
    );
  }

  const initial = displayName.slice(0, 1).toUpperCase();

  const points = profile.points ?? 0;
  const levelThresholds = [
    { level: 1, minPoints: 0, name: t('levels.newMember') },
    { level: 2, minPoints: 25, name: t('levels.activeMember') },
    { level: 3, minPoints: 100, name: t('levels.trustedMember') },
    { level: 4, minPoints: 250, name: t('levels.veteran') },
    { level: 5, minPoints: 500, name: t('levels.leader') },
  ];
  const currentLevelInfo = levelThresholds.reduce((prev, curr) =>
    points >= curr.minPoints ? curr : prev, levelThresholds[0]);
  const level = currentLevelInfo.level;
  const levelName = currentLevelInfo.name;
  const nextLevel = levelThresholds.find(l => l.level === level + 1);
  const nextLevelAt = nextLevel?.minPoints ?? null;
  const currentLevelAt = currentLevelInfo.minPoints;
  const progressPercent = nextLevelAt
    ? Math.min(100, ((points - currentLevelAt) / (nextLevelAt - currentLevelAt)) * 100)
    : 100;

  return (
    <div className="space-y-6">
      <Link to="/members">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('profile.backToMembers')}
        </Button>
      </Link>

      {/* Profile Header */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <Avatar className="h-24 w-24">
            <AvatarImage src={profile.profileImageUrl ?? undefined} />
            <AvatarFallback className="bg-primary/10 text-2xl text-primary">
              {initial}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <h1 className="text-2xl font-bold">{displayName}</h1>
                  <RoleBadge role={profile.communityRole} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('profile.level')} {level} - {levelName}
                </p>
                {nextLevelAt && (
                  <div className="mt-2 max-w-xs">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{points} / {nextLevelAt} pts</span>
                      <span>{t('profile.ptsToNext', { points: nextLevelAt - points, level: level + 1 })}</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(progressPercent)} aria-valuemin={0} aria-valuemax={100}>
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {!isOwnProfile && (
                <div className="flex gap-2">
                  <Button
                    variant={isFollowing ? 'outline' : 'default'}
                    size="sm"
                    className="gap-2"
                    onClick={handleToggleFollow}
                    disabled={isPending}
                  >
                    {isFollowing ? (
                      <>
                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                        {t('profile.unfollow')}
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" aria-hidden="true" />
                        {t('profile.follow')}
                      </>
                    )}
                  </Button>
                  <Link to={`/messages/${userId}`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                      {t('profile.sendMessage')}
                    </Button>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t('common.moreActions')}>
                        <MoreVertical className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="cursor-pointer text-amber-600 focus:text-amber-600"
                        onSelect={() => setShowReportModal(true)}
                      >
                        <Flag className="mr-2 h-4 w-4" />
                        {t('profile.reportUser')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive focus:text-destructive"
                        onSelect={() => setShowBlockModal(true)}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {t('profile.blockUser')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>

            {profile.bio && (
              <p className="mt-3 text-sm text-muted-foreground">{profile.bio}</p>
            )}

            {/* Interests */}
            {profile.interests && profile.interests.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5 justify-center sm:justify-start">
                {profile.interests.map((interest) => (
                  <span
                    key={interest}
                    className="rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs text-muted-foreground"
                  >
                    {t(`settings.interest.${interest}`, interest)}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground sm:justify-start">
              {profile.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {profile.location}
                </span>
              )}
              {profile.createdAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  {t('profile.joinedOn')} {format(new Date(profile.createdAt), 'MMM yyyy')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-6 sm:grid-cols-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{points}</p>
            <p className="text-sm text-muted-foreground">{t('profile.points')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{profile.postsCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.posts')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{connectionCounts?.followersCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.followers')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{connectionCounts?.followingCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.following')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="posts">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="posts">{t('profile.posts')}</TabsTrigger>
          <TabsTrigger value="followers">{t('profile.followers')}</TabsTrigger>
          <TabsTrigger value="following">{t('profile.following')}</TabsTrigger>
          <TabsTrigger value="badges">{t('profile.badges')}</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-4">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">{t('profile.noPosts')}</p>
          </div>
        </TabsContent>

        <TabsContent value="followers" className="mt-4">
          <div className="rounded-2xl border border-border bg-card">
            {followers?.followers && followers.followers.length > 0 ? (
              <div className="divide-y divide-border">
                {followers.followers.map((follower) => {
                  const name = follower.displayName ||
                    [follower.firstName, follower.lastName].filter(Boolean).join(' ') ||
                    'Anonymous';
                  const initials = name.slice(0, 1).toUpperCase();

                  return (
                    <Link
                      key={follower.userId}
                      to={`/profile/${follower.userId}`}
                      className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/50"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={follower.profileImageUrl ?? undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{name}</p>
                        <p className="text-xs text-muted-foreground">
                          {follower.points ?? 0} {t('profile.points').toLowerCase()}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">{t('profile.noFollowers')}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="following" className="mt-4">
          <div className="rounded-2xl border border-border bg-card">
            {following?.following && following.following.length > 0 ? (
              <div className="divide-y divide-border">
                {following.following.map((user) => {
                  const name = user.displayName ||
                    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                    'Anonymous';
                  const initials = name.slice(0, 1).toUpperCase();

                  return (
                    <Link
                      key={user.userId}
                      to={`/profile/${user.userId}`}
                      className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/50"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.profileImageUrl ?? undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{name}</p>
                        <p className="text-xs text-muted-foreground">
                          {user.points ?? 0} {t('profile.points').toLowerCase()}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">{t('profile.noFollowing')}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="badges" className="mt-4">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <Trophy className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="mt-4 text-muted-foreground">{t('profile.noBadges')}</p>
          </div>
        </TabsContent>
      </Tabs>

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetType="user"
        targetId={userId}
        targetName={displayName}
      />

      <BlockModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        userId={userId}
        userName={displayName}
      />
    </div>
  );
}
